import { Suspense, useState } from 'react';
import {
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Loading } from '@cotera/client/app/components';
import { Tool } from '@cotera/client/app/hooks/use-tools';
import { ToolConnections } from '../components/tool-connections/tool-connections-dialog';
import { generateToolMentionHtml } from '@cotera/client/app/components/tools/utils';
import { Button } from '../components/ui/button';
import { CheckCircle2, Copy, Check } from 'lucide-react';
import { writeToClipboard } from '@cotera/client/app/components/ui/tiptap/tiptap-ui/copy-to-clipboard-button/use-copy-to-clipboard';

/**
 * Generates HTML for a tool mention that can be pasted into TipTap
 * and automatically parsed as a mention node.
 */
const ToolSuccessModal: React.FC<{
  toolKey: string;
  displayName: string;
  isExisting: boolean;
  onClose: () => void;
}> = ({ toolKey, displayName, isExisting, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const html = generateToolMentionHtml(toolKey, displayName);
    const plainText = `@${displayName}`;
    await writeToClipboard(plainText, html);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <ModalPageView
      title={isExisting ? 'Tool Already Installed' : 'Tool Added'}
      className="p-4"
    >
      <div className="flex flex-col items-center justify-center text-center py-6">
        <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
        <h2 className="text-lg font-semibold mb-1">{displayName}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {isExisting
            ? 'is already installed in your workspace.'
            : 'has been added to your workspace.'}
        </p>
        <div className="bg-muted rounded-lg p-3 mb-6 w-full max-w-xs">
          <p className="text-xs text-muted-foreground mb-1">
            To use this tool in your agent, type or paste:
          </p>
          <div className="flex items-center gap-2">
            <p className="font-mono text-sm bg-background rounded px-2 py-1.5 flex-1">
              @{displayName}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Type <span className="font-mono">@</span> in the agent editor to
            search for tools.
          </p>
        </div>
        <Button onClick={onClose}>OK</Button>
      </div>
    </ModalPageView>
  );
};

export const NewToolModal: React.FC<{
  onComplete: (tool: Tool & { isExisting?: boolean }) => Promise<void>;
  onClose: () => void;
  /** Optional tool key to automatically navigate to */
  initialToolKey?: string;
}> = ({ onComplete, onClose, initialToolKey }) => {
  const modalManager = useModalManager((s) => s.actions);

  const handleSelect = async (tool: Tool & { isExisting?: boolean }) => {
    if (!tool.isExisting) {
      await onComplete(tool);
    }
    // Replace with a smaller success modal
    modalManager.replace({
      view: () => (
        <ToolSuccessModal
          toolKey={tool.name}
          displayName={tool.displayName}
          isExisting={tool.isExisting ?? false}
          onClose={onClose}
        />
      ),
      size: 'auto',
    });
  };

  return (
    <ModalPageView
      title="Install Tool"
      subtitle="Search for tools to add to your Cotera instance."
      className="p-4"
    >
      <Suspense
        fallback={
          <div className="h-[60vh] flex items-center justify-center">
            <Loading.Spinner />
          </div>
        }
      >
        <ToolConnections
          onSelect={handleSelect}
          className="h-[60vh]"
          initialToolKey={initialToolKey}
        />
      </Suspense>
    </ModalPageView>
  );
};
